import type { ProgressBarStepId } from '@ctct/state-client-layer';

import { PayflowProgressBarService } from './services/PayflowProgressBarService';
import type { Init, Flags } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type PayflowProgressBarData = MergedDataReturnType<[PayflowProgressBarService]>;

export type PayflowProgressBarHandlers = MergedHandlersReturnType<[PayflowProgressBarService]>;

type TApi = ReturnType<PayflowProgressBarService['api']>;

export class PayflowProgressBarManager extends WithApi<TApi> {
  public flags: Flags | null = null;

  public payflowProgressBarInit = ({ pageStore, queries, flags, t, billingStrategy, flowId }: Init) => {
    this.flags = flags;

    const [planGroupQuery, addOnsQuery] = queries;
    const pending = planGroupQuery.isPending || addOnsQuery.isPending;
    const error = planGroupQuery.error || addOnsQuery.error;

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
      } as const;
    }

    const { addVisitedStep, isFinishedStep, isVisitedNotFinishedStep, isVisitedStep } = this;

    const payflowProgressBarService = new PayflowProgressBarService(
      pageStore,
      planGroupQuery.data,
      addOnsQuery.data,
      flags,
      t,
      billingStrategy,
      {
        addVisitedStep,
        isVisitedStep,
        isFinishedStep,
        isVisitedNotFinishedStep,
      },
      flowId
    );

    this.initApi({
      ...payflowProgressBarService.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...payflowProgressBarService.data(),
      },
      handlers: {
        ...payflowProgressBarService.handlers(),
      },
      lifecycle: {
        onMount() {
          payflowProgressBarService.onMount();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };

  private visitedSteps = new Set<ProgressBarStepId>();

  private lastVisitedStep: ProgressBarStepId | null = null;

  private addVisitedStep = (id: ProgressBarStepId): void => {
    if (!this.visitedSteps.has(id)) {
      this.visitedSteps.add(id);
      this.lastVisitedStep = id;
    }
  };

  private isVisitedStep = (id: ProgressBarStepId): boolean => this.visitedSteps.has(id);

  private isFinishedStep = (id: ProgressBarStepId): boolean => this.visitedSteps.has(id) && this.lastVisitedStep !== id;

  private isVisitedNotFinishedStep = (id: ProgressBarStepId): boolean => this.lastVisitedStep === id;
}
